import {
  Component,
  OnInit,
  OnChanges,
  Input,
  ViewChild,
  ElementRef,
} from '@angular/core'
import { DecimalPipe } from '@angular/common'
import { TranslateService } from '@ngx-translate/core'
import {
  scaleOrdinal,
  schemeCategory10,
  select,
  arc as d3Arc,
  pie as d3Pie,
  mouse,
} from 'd3'
import { Colors } from '../../common/config/constants'
import { ContextService } from '../../services'

@Component({
  standalone: false,
  selector: 'app-pie-chart-widget',
  templateUrl: './pie-chart-widget.component.html',
  styleUrls: ['./pie-chart-widget.component.scss'],
})
export class PieChartWidgetComponent implements OnInit, OnChanges {
  private colorFunc

  @Input() title: ''
  @Input() unit = ''
  @Input() data = []
  @Input() isMultiple = false
  @Input() compareOptions: any = {}
  @Input() colors: any = null
  @Input() icon: string = null
  @Input() highlight: string[] = []
  @Input() labels = []
  @Input() stats
  @ViewChild('chart', { static: true }) chartElem: ElementRef

  get referenceData(): any[] {
    if (!this.isMultiple) {
      return this.data || []
    }
    const refData = this.data[0]
    if (!refData) {
      return []
    }
    return Array.isArray(refData) ? refData : [refData]
  }

  get comparisonData(): any[] | null {
    if (!this.isMultiple) {
      return null
    }
    const cmpData = this.data[1]
    if (!cmpData) {
      return null
    }
    return Array.isArray(cmpData) ? cmpData : [cmpData]
  }

  get isVisitsAvailable() {
    return this.context.isVisitsAvailable()
  }

  constructor(
    private decimalPipe: DecimalPipe,
    private translateService: TranslateService,
    private context: ContextService,
  ) {}

  ngOnInit() {
    this.ngOnChanges(null)
  }

  ngOnChanges(changes) {
    if (this.data && this.data.length) {
      if (changes === null) {
        this.drawChart()
        return null
      }
      for (const key of Object.keys(changes)) {
        if (changes[key].currentValue) {
          if (
            JSON.stringify(changes[key].previousValue) !==
            JSON.stringify(changes[key].currentValue)
          ) {
            this.drawChart()
            return null
          }
        }
      }
    }
  }

  private drawChart() {
    if (!Array.isArray(this.referenceData) || !this.referenceData.length) {
      return
    }
    this.removeContent()
    const width = this.chartElem.nativeElement.offsetWidth || 228
    const height = width
    const thickness = 12
    const labelMargin = 26

    const radius = Math.min(width, height) / 2
    if (this.colors) {
      this.colorFunc = scaleOrdinal(this.colors)
    } else {
      this.colorFunc = scaleOrdinal(schemeCategory10)
    }

    const unit = this.unit

    const logoSize = {
      width: 40,
      height: 40,
    }

    const svg = select(this.chartElem.nativeElement)
      .append('svg')
      .attr('class', 'pie')
      .attr('width', width)
      .attr('height', height + (this.comparisonData ? 2 * labelMargin : 0))

    const center = {
      x: width / 2,
      y: height / 2 + (this.comparisonData ? 2 * labelMargin : 0),
    }

    // wrapper for centering
    const centerG = svg
      .append('g')
      .attr('class', 'center')
      .attr('transform', 'translate(' + center.x + ',' + center.y + ')')

    // center image
    if (this.icon) {
      centerG
        .append('svg:image')
        .attr('xlink:href', this.icon)
        .attr('height', logoSize.height + 'px')
        .attr('width', logoSize.width + 'px')
        .attr(
          'transform',
          'translate(-' + logoSize.width / 2 + ',-' + logoSize.height / 2 + ')',
        )
    }

    const tooltip = select(this.chartElem.nativeElement)
      .append('div')
      .attr('class', 'tooltip')
      .style('position', 'absolute')
      .style('display', 'none')

    const arc = d3Arc()
      .innerRadius(radius - thickness)
      .outerRadius(radius)

    const pie = d3Pie()
      .value((d: any) => {
        return this.unit === '%' ? d.perc : d.value
      })
      .sort(null)

    // arc container
    centerG
      .append('g')
      .attr('class', 'reference-data')
      .selectAll('path')
      .data(pie(this.referenceData))
      .enter()
      .append('path')
      .attr('d', arc as any)
      .attr('fill', (d: any, i: any) => this.getElemColor(d, i))
      .on('mouseover', (d: any) => {
        const value = this.decimalPipe.transform(d.data.value, '1.0-0')
        const percentage =
          d.data.perc !== undefined && d.data.perc !== null
            ? this.decimalPipe.transform(d.data.perc, '1.0-2')
            : this.decimalPipe.transform(
                ((d.data.value / this.stats) * 100).toFixed(1),
                '1.0-0',
              )
        const categoryLabel = d.data.category
          ? this.translateService.instant(d.data.category)
          : ''
        let template = `<h5>${categoryLabel}</h5>
          <p class="value">${value} <span>${unit}</span></p>
        `
        if (this.isVisitsAvailable && this.stats) {
          template += `<p class="value">${percentage}% <span>${this.translateService.instant(
            'over the total of',
          )} ${unit}</span></p>`
        }
        const mouseCoords = mouse(this.chartElem.nativeElement)
        if (mouseCoords[0] <= width / 2) {
          mouseCoords[0] += 30
        } else {
          mouseCoords[0] -= mouseCoords[0] * 0.2
        }
        tooltip
          .html(template)
          .style('display', 'block')
          .style('left', mouseCoords[0] + 'px')
          .style('top', mouseCoords[1] + 'px')
      })
      .on('mousemove', (d: any) => {
        const mouseCoords = mouse(this.chartElem.nativeElement)
        if (mouseCoords[0] <= width / 2) {
          mouseCoords[0] += 30
        } else {
          mouseCoords[0] -= mouseCoords[0] * 0.2
        }
        tooltip
          .style('left', mouseCoords[0] + 'px')
          .style('top', mouseCoords[1] + 'px')
      })
      .on('mouseout', function (d: any) {
        tooltip.style('display', 'none')
      })

    if (this.comparisonData) {
      const comparisonRadius = radius - 1.5 * thickness
      const comparisonArc = d3Arc()
        .innerRadius(comparisonRadius - thickness)
        .outerRadius(comparisonRadius)
      centerG
        .append('g')
        .attr('class', 'comparison-data')
        .selectAll('path')
        .data(pie(this.comparisonData))
        .enter()
        .append('path')
        .attr('d', comparisonArc)
        .attr('fill', (d: any, i: any) => this.getElemColor(d, i))
        .on('mouseover', function (d: any) {
          tooltip.style('display', 'block')
        })
        .on('mousemove', (d: any) => {
          const mouseCoords = mouse(this.chartElem.nativeElement)
          if (mouseCoords[0] <= width / 2) {
            mouseCoords[0] += 30
          } else {
            mouseCoords[0] -= mouseCoords[0] * 0.2
          }
          const value = this.decimalPipe.transform(d.data.value, '1.0-0')
          const percentage =
            d.data.perc !== undefined && d.data.perc !== null
              ? this.decimalPipe.transform(d.data.perc, '1.0-2')
              : this.decimalPipe.transform(
                  ((d.data.value / this.stats) * 100).toFixed(1),
                  '1.0-0',
                )
          const categoryLabel = d.data.category
            ? this.translateService.instant(d.data.category)
            : ''
          let template = `<h5>${categoryLabel}</h5>
          <p class="value">${value} <span>${unit}</span></p>
        `
          if (this.isVisitsAvailable && this.stats) {
            template += `<p class="value">${percentage}% <span>${this.translateService.instant(
              'over the total of',
            )} ${unit}</span></p>`
          }
          tooltip
            .html(template)
            .style('left', mouseCoords[0] + 'px')
            .style('top', mouseCoords[1] + 'px')
        })
        .on('mouseout', function (d: any) {
          tooltip.style('display', 'none')
        })
    }

    const textAnchors = [
      { x: `${width - 4}px`, y: 0.75, alignment: 'end' },
      { x: '4px', y: 0.75, alignment: 'start' },
    ]
    this.referenceData.forEach((elem, idx) => {
      if (!elem?.category) return
      const labelG = svg.append('g').attr('class', 'legend reference')
      // datum label: name
      labelG
        .append('text')
        .attr('text-anchor', textAnchors[idx].alignment)
        .attr('dy', `${textAnchors[idx].y}em`)
        .attr('dx', textAnchors[idx].x)
        .attr('class', 'legendTitle')
        .text(`% ${this.translateService.instant(elem.category)}`)
      // datum label: percent
      labelG
        .append('text')
        .attr('text-anchor', textAnchors[idx].alignment)
        .attr('dy', `${textAnchors[idx].y + 1}em`)
        .attr('dx', textAnchors[idx].x)
        .attr('class', 'legendValue')
        .attr('fill', this.getElemColor({ data: elem }, idx))
        .text(Math.round(elem.perc))
    })

    if (this.comparisonData) {
      const legendConfig = this.compareOptions.legendConfig || [
        { x: +6, y: -54, alignment: 'start' },
        { x: -6, y: -54, alignment: 'end' },
      ]
      this.comparisonData.forEach((elem, idx) => {
        centerG
          .append('g')
          .attr('class', 'legend comparison')
          .append('text')
          .attr('text-anchor', legendConfig[idx].alignment)
          .attr('dy', legendConfig[idx].y)
          .attr('dx', legendConfig[idx].x)
          .attr('class', 'legendValue')
          .attr('fill', this.getElemColor({ data: elem }, idx))
          .text(Math.round(elem.perc))
      })
      const labelConfig = [{ y: -(height / 2) - 11 }, { y: -24 }]
      this.labels.forEach((label, i) => {
        const svgLabel = centerG
          .append('text')
          .attr('class', `label ${i % 2 === 0 ? 'reference' : 'comparison'}`)
          .attr('text-anchor', 'middle')
          .attr('dy', labelConfig[i].y)
          .text(this.translateService.instant(label))
        const originalLabel = this.translateService.instant(label)
        while (svgLabel.node().getComputedTextLength() > 120) {
          label = label.slice(0, -1)
          svgLabel.node().textContent = `${label}...`
        }
        svgLabel.append('title').text(originalLabel)
      })
    }
  }

  private removeContent() {
    const node = this.chartElem.nativeElement
    while (node.firstChild) {
      node.removeChild(node.firstChild)
    }
  }

  private getElemColor(d, i) {
    let elemColor = this.colorFunc(i)
    if (this.highlight.length && this.highlight.indexOf(d.data.id) === -1) {
      elemColor = Colors.colorN30
    }
    return elemColor
  }
}
